'use client';

/**
 * «Устарело» on a page, a block's audio, a sheet or an export, with the
 * «Обновить» button next to it (edits.html: stale → highlight + «обновить»).
 * «Обновить» is an add-on order with its own estimate; while the story
 * generates or a print order is open the button is disabled with the reason.
 */
import { useTranslations } from 'next-intl';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import type { ArtifactNote, EditEstimate, ForbiddenReason } from '../types';

export interface StaleBadgeProps {
  /** number of stale artifacts behind the badge (pages, blocks) */
  count?: number;
  /** why: «перезапишите» for a recorded block, «новая версия» for a publication … */
  note?: ArtifactNote;
  className?: string;
}

export function StaleBadge({ count, note, className }: StaleBadgeProps) {
  const t = useTranslations('stories.edits');
  return (
    <Badge
      variant="outline"
      data-testid="stale-badge"
      data-note={note ?? ''}
      className={`gap-1 border-warning/60 bg-warning/10 text-warning ${className ?? ''}`}
    >
      <AlertTriangle className="size-3" />
      {count && count > 1 ? t('staleCount', { count }) : t('stale')}
      {note ? <span className="font-normal">· {t(`notes.${note}`)}</span> : null}
    </Badge>
  );
}

export interface StaleUpdateProps extends StaleBadgeProps {
  onUpdate?: () => void;
  /** the add-on order estimate («обновить» shows the credits) */
  estimate?: EditEstimate;
  /** a guard says no: the button is disabled with the reason */
  forbiddenReason?: ForbiddenReason;
  pending?: boolean;
}

export function StaleUpdate({ onUpdate, estimate, forbiddenReason, pending, ...badge }: StaleUpdateProps) {
  const t = useTranslations('stories.edits');
  const disabled = !onUpdate || !!forbiddenReason || pending;
  const hint = forbiddenReason ? t(`forbidden.${forbiddenReason}`) : undefined;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="stale-update">
      <StaleBadge {...badge} />
      <Button
        size="sm"
        variant="outline"
        disabled={disabled}
        title={hint}
        onClick={onUpdate}
        data-testid="stale-update-button"
      >
        <RefreshCw className={`size-3.5 ${pending ? 'animate-spin' : ''}`} />
        {estimate && estimate.credits > 0 ? t('updateFor', { credits: estimate.credits }) : t('update')}
      </Button>
      {hint ? <span className="text-xs text-muted-foreground" data-testid="stale-update-hint">{hint}</span> : null}
    </div>
  );
}
